import { api } from "./client";
import type {
  OrderDetail,
  OrderHistory,
  OrderListPage,
  OrderStatus,
  OrderTransitionResult,
} from "./types";

/** Query parameters the admin order listing actually accepts.
 *
 * Every one of them is applied by the server. There is no client-side
 * narrowing anywhere in this module or the screens that use it: the result is
 * one page, so a filter applied here would search the twenty-five rows in
 * front of the administrator and report that an order on page two does not
 * exist.
 */
export interface OrderListQuery {
  /** Order number, customer name, or phone number with separators ignored. */
  search?: string;
  status?: string;
  /** An ISO instant WITH an offset. Inclusive lower bound.
   *
   *  An instant and not a date: the calendar day belongs to the operator, who
   *  sees timestamps in their own timezone, so the browser resolves the day
   *  they picked into the two moments it really spans. See `orders/dateRange`. */
  created_from?: string;
  /** An ISO instant WITH an offset. EXCLUSIVE upper bound — the start of the
   *  day after the one the operator chose, so consecutive days tile exactly. */
  created_before?: string;
  page?: number;
  page_size?: number;
}

/**
 * A key identifying ONE intent to move an order, for the backend's
 * `Idempotency-Key` header.
 *
 * Generated fresh each time an administrator asks for a transition, and reused
 * by nothing: this client never retries a mutation on its own, so a second key
 * is only ever created by a second deliberate press — which IS a second intent
 * and should be treated as one. What the key protects against is the request
 * that was delivered and whose answer was lost; the backend answers a repeat of
 * that key with the original result instead of moving the order twice.
 *
 * `crypto.getRandomValues` rather than `crypto.randomUUID` because the latter
 * is absent in some test environments, and a key that silently fell back to
 * `Math.random` would be a deduplication guarantee that is not one.
 */
export function newIdempotencyKey(): string {
  const bytes = new Uint8Array(16); // 128 bits, as the backend documents.
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export const ordersApi = {
  list: (query: OrderListQuery, signal?: AbortSignal) =>
    api.get<OrderListPage>("/admin/orders", { ...query }, signal),

  detail: (orderId: string, signal?: AbortSignal) =>
    api.get<OrderDetail>(`/admin/orders/${orderId}`, undefined, signal),

  history: (orderId: string, signal?: AbortSignal) =>
    api.get<OrderHistory>(`/admin/orders/${orderId}/history`, undefined, signal),

  /**
   * Move one order to one state, once.
   *
   * `target` is a state the backend named in `allowed_transitions`. Nothing
   * here checks whether it is legal — that is the server's decision and it
   * makes it again regardless of what this client believed, which is what makes
   * a stale screen safe rather than merely unlikely.
   */
  transition: (
    orderId: string,
    target: OrderStatus,
    reason: string,
    idempotencyKey: string,
  ) =>
    api.post<OrderTransitionResult>(
      `/admin/orders/${orderId}/transition`,
      { target_status: target, reason },
      { "Idempotency-Key": idempotencyKey },
    ),
};
